import type {
  CreateDocumentAction,
  CreateEventAction,
  InboxEmail,
} from '../../types/inbox.types';

export type SummaryBlock =
  | { type: 'heading'; text: string }
  | { type: 'subheading'; text: string }
  | { type: 'paragraph'; text: string }
  | { type: 'list'; items: string[] }
  | { type: 'orderedList'; items: string[] };

type Props = {
  summary?: string | null;
  email?: InboxEmail | null;
};

export function useDocumentSummaryCard(props: Props) {
  let summary = props.summary?.trim() || '';

  if (!summary && props.email) {
    const email = props.email;
    const items: string[] = [];

    const sender = email.from_name || email.from_email;
    items.push(`**Email reçu de ${sender}** : « ${email.subject} »`);

    if (email.attachments && email.attachments.length > 0) {
      items.push(
        `**Documents joints (${email.attachments.length})** : ${email.attachments.map((a) => a.filename).join(', ')}`
      );
    }

    if (email.parsedActions && email.parsedActions.length > 0) {
      const quotes = email.parsedActions.filter(
        (a): a is CreateDocumentAction => a.type === 'CREATE_DOCUMENT' && Boolean(a.isQuote)
      );
      if (quotes.length > 0) {
        items.push(
          `**Devis identifiés (${quotes.length})** : ${quotes
            .map(
              (q) =>
                `${q.title}${q.suggestedAmount ? ` (${q.suggestedAmount.toLocaleString('fr-FR')} €)` : ''}`
            )
            .join(', ')}`
        );
      }

      const events = email.parsedActions.filter(
        (a): a is CreateEventAction => a.type === 'CREATE_EVENT'
      );
      if (events.length > 0) {
        items.push(
          `**Rendez-vous proposés (${events.length})** : ${events.map((e) => e.title).join(', ')}`
        );
      }
    }

    summary = items.map((it) => `- ${it}`).join('\n');
  }

  const hasSummary = Boolean(summary && summary.trim().length > 0);

  return {
    hasSummary,
    summary,
    blocks: parseSummary(summary),
  };
}

const BULLET_PATTERN = /^[-*]\s+/;
const NUMBERED_PATTERN = /^\d+\.\s+/;

/** Turns the mini-markdown summary (##, ###, bullets, numbered items) into renderable blocks. */
export function parseSummary(summary: string): SummaryBlock[] {
  const lines = summary.replace(/\\r\\n/g, '\n').replace(/\\n/g, '\n').split('\n');
  const blocks: SummaryBlock[] = [];

  function pushListItem(type: 'list' | 'orderedList', text: string) {
    const last = blocks[blocks.length - 1];
    if (last && last.type === type) {
      last.items.push(text);
    } else {
      blocks.push({ type, items: [text] });
    }
  }

  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (!line) continue;

    if (line.startsWith('### ')) {
      blocks.push({ type: 'subheading', text: line.slice(4) });
    } else if (line.startsWith('## ')) {
      blocks.push({ type: 'heading', text: line.slice(3) });
    } else if (BULLET_PATTERN.test(line)) {
      pushListItem('list', line.replace(BULLET_PATTERN, ''));
    } else if (NUMBERED_PATTERN.test(line)) {
      pushListItem('orderedList', line.replace(NUMBERED_PATTERN, ''));
    } else {
      blocks.push({ type: 'paragraph', text: line });
    }
  }

  return blocks;
}
